<template>
  <div class="chart">
    <apexchart
      :height="chartOptions.chart.height" 
      :options="chartOptions"
      :series="series"
      :key="chartKey" 
    ></apexchart>
  </div>
</template>

<script setup>
import { mainStore } from '@/utils/telemetryData';
import { ref, computed, watch } from 'vue';

const props = defineProps({
  data: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'line',
  },
});

const chartKey = ref(0); 
const xaxisRange = ref(10000);

function downsample(data, maxPoints = 200) {
  const step = Math.ceil(data.length / maxPoints);
  return data.filter((_, i) => i % step === 0);
}

const series = computed(() => {
  if (props.data.includes('_tyre')) {
    const prefix = props.data.split('_')[0];
    return [
      {
        name: `Tyre_Temp_${prefix}1`,
        data: Array.from(mainStore.getters.historyForDiagram(`Tyre_Temp_${prefix}1`)),
      },
      {
        name: `Tyre_Temp_${prefix}3`,
        data: Array.from(mainStore.getters.historyForDiagram(`Tyre_Temp_${prefix}3`)),
      },
      {
        name: `Tyre_Temp_${prefix}5`,
        data: Array.from(mainStore.getters.historyForDiagram(`Tyre_Temp_${prefix}5`)),
      },
      {
        name: `Tyre_Temp_${prefix}7`,
        data: Array.from(mainStore.getters.historyForDiagram(`Tyre_Temp_${prefix}7`)),
      }
    ];
  }

  if (props.data === 'MotorTorque') {
    return [
      {
        name: 'Motor_Torque1',
        data: Array.from(mainStore.getters.historyForDiagram('Motor_Torque1')),
      },
      {
        name: 'Motor_Torque2',
        data: Array.from(mainStore.getters.historyForDiagram('Motor_Torque2')),
      },
      {
        name: 'Motor_Torque3',
        data: Array.from(mainStore.getters.historyForDiagram('Motor_Torque3')),
      },
      {
        name: 'Motor_Torque4',
        data: Array.from(mainStore.getters.historyForDiagram('Motor_Torque4')),
      },
    ];
  }

  return [{
    name: props.label,
    data: Array.from(mainStore.getters.historyForDiagram(props.data)),
  }];
});

const chartOptions = computed(() => {
  const defaultOptions = {
    chart: {
      id: `${props.label}-chart`,
      type: props.type,
      height: 150, 
      animations: {
        enabled: false,
        easing: 'linear',
        animateGradually: {
            enabled: false,
            delay: 150
        },
        dynamicAnimation: {
          enabled: false,
          speed: 500,
        },
      },
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: true,
      },
    },
    theme: {
      mode: 'dark',
      palette: 'palette7',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
    },
    fill:{
      type:'solid'
    },
    title: {
      text: props.label,
      align: 'left',
      offsetY: 10,
      offsetX: 5,
    },
    markers: {
      size: 0,
    },
    xaxis: {
      type: 'datetime',
      range: xaxisRange.value,
      tickAmount: 6,
      labels: {
        datetimeFormatter: {
          second: 'HH:mm:ss'
      },
      formatter: (value) => {
        const date = new Date(value);
        return `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
        },
      },
    },
  };

  if (props.data.includes('_tyre')) {
    return {
      ...defaultOptions,
      chart: {
        ...defaultOptions.chart,
        id: props.data+'-chart',
        height: 250, 
      },
      yaxis: {
        min: 0,
        max: 200,
        tickAmount: 6
      },
    };
  }

  if (props.data === 'MotorTorque') {
    return {
      ...defaultOptions,
      chart: {
        ...defaultOptions.chart,
        id: props.data+'-chart',
        height: 250, 
      },
      yaxis: {
        min: 0,
        max: 500,
        tickAmount: 6
      },
    };
  }

  if (props.data === 'DRS') {
    return {
      ...defaultOptions,
      chart: {
        ...defaultOptions.chart,
        id: 'DRS chart',
      },
      stroke: {
        curve: 'stepline',
      },
      yaxis: {
        min: 0,
        max: 1,
        tickAmount: 1,
        labels: {
          formatter: (value) => (value === 1 ? 'On' : 'Off'),
        },
      },
      theme: {
        ...defaultOptions.theme,
        monochrome: {
          enabled: true,
          color: '#007678',
        },
      },
    };
  }

  if (props.data === 'SteeringAngle') {
    return {
      ...defaultOptions,
      chart: {
        ...defaultOptions.chart,
        id: 'Steering-angle-chart',
      },
      title: {
        text: 'Steering angle',
        align: 'left',
      },
      yaxis: {
        min: 0,
        max: 360,
        labels: {
          formatter: (value) => `${value}°`,
        },
      },
    };
  }
  
  return defaultOptions;
});

// Store változások figyelése
watch(() => mainStore.getters.historyForDiagram(props.data), () => {
  chartKey.value++; 
}, { deep: true });
</script>